import { useState } from 'react';
import { InteractiveDemo } from './InteractiveDemo';
import {
    Zap,
    Brain,
    ArrowRight,
    Lock,
    Cpu,
    Activity,
    Eye,
    Network,
    Bot,
    Shield,
    GitBranch,
    MessageSquare,
    Clock,

    Fingerprint,
    Plug,
    Gauge,
    Sparkles,
    Rocket,
    RefreshCw,
    Database,
    CheckCircle,
    AlertTriangle,
    DollarSign,
    Users,
    Minus,
    Plus,
    Search,
    FileText,
    Crosshair,
    Target,
    BookOpen
} from 'lucide-react';
import logoImage from '../assets/logo.png';

interface LandingPageProps {
    onGetStarted: () => void;
}

/* ─── Demo Request Modal ─────────────────────────── */
interface DemoModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSubmitSuccess?: () => void;
}

function DemoRequestModal({ isOpen, onClose, onSubmitSuccess }: DemoModalProps) {
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        company: '',
        title: '',
        message: '',
    });
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isSubmitted, setIsSubmitted] = useState(false);

    if (!isOpen) return null;

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setIsSubmitting(true);

        const payload = {
            ...formData,
            source: window.location.href,
            type: 'demo_request',
            timestamp: new Date().toISOString(),
        };

        // Always save to localStorage first so we never lose a lead
        try {
            const leads = JSON.parse(localStorage.getItem('zoro_leads') || '[]');
            leads.push(payload);
            localStorage.setItem('zoro_leads', JSON.stringify(leads));
        } catch { /* Private browsing or storage full */ }

        try {
            // Send as JSON string with text/plain content type
            // Apps Script doPost(e) uses JSON.parse(e.postData.contents) on line 3
            // text/plain avoids CORS preflight while still delivering parseable JSON
            await fetch('https://script.google.com/macros/s/AKfycbyW8YcCYNraWslQSB8w6sZ_YqhHXAGlzUWRwCd3as8DmTH7mbCi7ZYZ_osjRpugWLIuug/exec', {
                method: 'POST',
                mode: 'no-cors',
                headers: { 'Content-Type': 'text/plain' },
                body: JSON.stringify(payload),
            });
        } catch {
            // Network error — already saved to localStorage above
            console.warn('[Zoro] Demo request saved locally (network error)');
        }

        setIsSubmitting(false);
        setIsSubmitted(true);

        // Auto-close after showing success
        setTimeout(() => {
            setIsSubmitted(false);
            setFormData({ name: '', email: '', company: '', title: '', message: '' });
            onClose();
            if (onSubmitSuccess) onSubmitSuccess();
        }, 2000);
    };

    const inputClass = "w-full px-4 py-3 rounded-xl bg-slate-800/80 border border-slate-700 text-white text-sm placeholder:text-slate-500 focus:outline-none focus:border-primary-500 focus:ring-1 focus:ring-primary-500/50 transition-all";

    return (
        <div
            className="fixed inset-0 z-[100] flex items-center justify-center p-4"
            onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
        >
            {/* Backdrop */}
            <div className="absolute inset-0 bg-black/70" />

            {/* Modal */}
            <div className="relative w-full max-w-lg rounded-2xl bg-slate-900 border border-slate-700 shadow-2xl shadow-black/50 overflow-hidden animate-slide-up">
                {/* Glow */}
                <div className="hidden md:block absolute top-0 left-1/2 -translate-x-1/2 w-64 h-32 bg-primary-500/20 rounded-full blur-[40px] -z-10" />

                {/* Close button */}
                <button
                    onClick={onClose}
                    className="absolute top-4 right-4 text-slate-500 hover:text-white transition-colors text-lg"
                >
                    ✕
                </button>

                {isSubmitted ? (
                    <div className="p-12 text-center">
                        <div className="w-16 h-16 rounded-full bg-emerald-500/20 flex items-center justify-center mx-auto mb-4">
                            <CheckCircle className="w-8 h-8 text-emerald-400" />
                        </div>
                        <h3 className="text-2xl font-bold text-white mb-2">You're in!</h3>
                        <p className="text-slate-400 text-sm">We'll reach out within 24 hours to schedule your demo.</p>
                    </div>
                ) : (
                    <form onSubmit={handleSubmit} className="p-8">
                        <div className="mb-6">
                            <h3 className="text-2xl font-bold text-white mb-1">Start a Guided Pilot</h3>
                            <p className="text-sm text-slate-400">See how Zoro can automate your security operations.</p>
                        </div>

                        <div className="space-y-4">
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-xs font-medium text-slate-400 mb-1.5">Full Name *</label>
                                    <input
                                        type="text"
                                        required
                                        placeholder="John Smith"
                                        className={inputClass}
                                        value={formData.name}
                                        onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                                    />
                                </div>
                                <div>
                                    <label className="block text-xs font-medium text-slate-400 mb-1.5">Job Title</label>
                                    <input
                                        type="text"
                                        placeholder="SOC Manager"
                                        className={inputClass}
                                        value={formData.title}
                                        onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                                    />
                                </div>
                            </div>

                            <div>
                                <label className="block text-xs font-medium text-slate-400 mb-1.5">Work Email *</label>
                                <input
                                    type="email"
                                    required
                                    placeholder="john@company.com"
                                    className={inputClass}
                                    value={formData.email}
                                    onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                                />
                            </div>

                            <div>
                                <label className="block text-xs font-medium text-slate-400 mb-1.5">Company *</label>
                                <input
                                    type="text"
                                    required
                                    placeholder="Acme Security Inc."
                                    className={inputClass}
                                    value={formData.company}
                                    onChange={(e) => setFormData(prev => ({ ...prev, company: e.target.value }))}
                                />
                            </div>

                            <div>
                                <label className="block text-xs font-medium text-slate-400 mb-1.5">Anything specific you'd like to see?</label>
                                <textarea
                                    rows={3}
                                    placeholder="e.g. We use Splunk + CrowdStrike and handle ~3,000 alerts/day..."
                                    className={`${inputClass} resize-none`}
                                    value={formData.message}
                                    onChange={(e) => setFormData(prev => ({ ...prev, message: e.target.value }))}
                                />
                            </div>
                        </div>

                        <button
                            type="submit"
                            disabled={isSubmitting}
                            className="w-full mt-6 py-3.5 rounded-xl bg-primary-600 hover:bg-primary-500 disabled:bg-primary-600/50 text-white font-bold text-sm transition-all flex items-center justify-center gap-2"
                        >
                            {isSubmitting ? (
                                <>
                                    <RefreshCw className="w-4 h-4 animate-spin" />
                                    Submitting...
                                </>
                            ) : (
                                <>
                                    Start a Guided Pilot <ArrowRight className="w-4 h-4" />
                                </>
                            )}
                        </button>

                        <p className="text-center text-xs text-slate-600 mt-4">
                            No spam. We'll respond within 24 hours.
                        </p>
                    </form>
                )}
            </div>
        </div>
    );
}

/* ─── Integrations Section (D3-style) ────────────── */
function IntegrationsSection() {
    const [activeCategory, setActiveCategory] = useState(0);

    const categories = [
        {
            title: 'Vendor-Agnostic',
            desc: 'Zoro integrates with every major security vendor—SIEM, EDR, ITSM, IAM, cloud, and more. We don\'t compete with your stack. We make it work harder for you.',
            tools: ['Splunk', 'CrowdStrike', 'Sentinel', 'Okta', 'AWS', 'ServiceNow', 'PagerDuty', 'SentinelOne'],
            bullets: [
                { highlight: 'Fully featured', text: ' integrations with every major vendor' },
                { highlight: 'Keep your stack', text: '—Zoro adapts to you, not the other way around' },
                { highlight: 'No vendor lock-in', text: '—switch tools anytime, Zoro still works' },
            ],
            mockTitle: 'Connected Tools',
            mockItems: [
                { name: 'Splunk', status: 'Connected', color: 'emerald' },
                { name: 'CrowdStrike', status: 'Connected', color: 'emerald' },
                { name: 'Okta', status: 'Connected', color: 'emerald' },
                { name: 'ServiceNow', status: 'Connected', color: 'emerald' },
            ],
        },
        {
            title: 'Unlimited',
            desc: 'Connect any security technology from any stack. Whether your tools are on-premise or in the cloud, Zoro normalizes data and operationalizes your full tooling spectrum—at any alert volume.',
            tools: ['Jira', 'Slack', 'Teams', 'Tenable', 'Qualys', 'Carbon Black', 'Proofpoint', 'Zscaler'],
            bullets: [
                { highlight: 'If we don\'t have it', text: ', we\'ll build it—custom connectors available' },
                { highlight: 'Connect across', text: ' multi-cloud and hybrid environments' },
                { highlight: 'Every integration', text: ' included on every plan—no add-on fees' },
            ],
            mockTitle: 'Integration Pipeline',
            mockItems: [
                { name: 'Cloud SIEM', status: 'Ingesting', color: 'blue' },
                { name: 'On-Prem EDR', status: 'Ingesting', color: 'blue' },
                { name: 'SaaS IAM', status: 'Syncing', color: 'purple' },
                { name: 'Ticketing', status: 'Active', color: 'emerald' },
            ],
        },
        {
            title: 'Zero-Config',
            desc: 'Unlike legacy SOAR tools that need months of professional services, Zoro\'s agent configures integrations automatically. Enter your API key, the agent handles the rest—query schemas, field mapping, and normalization.',
            tools: ['Auto-Discovery', 'Schema Mapping', 'Field Normalization', 'Health Monitoring', 'Rate Limiting', 'Error Recovery', 'Retry Logic', 'Audit Logging'],
            bullets: [
                { highlight: '1-click setup', text: '—enter API key, agent does the rest' },
                { highlight: 'Self-healing', text: ' connections that auto-recover from failures' },
                { highlight: 'Full audit trail', text: ' for every API call across every tool' },
            ],
            mockTitle: 'Auto-Configuration',
            mockItems: [
                { name: 'API Discovery', status: 'Complete', color: 'emerald' },
                { name: 'Schema Mapping', status: 'Complete', color: 'emerald' },
                { name: 'Field Normalization', status: 'Complete', color: 'emerald' },
                { name: 'Health Check', status: 'Passing', color: 'emerald' },
            ],
        },
    ];

    const active = categories[activeCategory];

    const colorMap: Record<string, { text: string; bg: string }> = {
        emerald: { text: 'text-emerald-400', bg: 'bg-emerald-400' },
        blue: { text: 'text-blue-400', bg: 'bg-blue-400' },
        purple: { text: 'text-purple-400', bg: 'bg-purple-400' },
    };

    return (
        <section id="integrations" className="py-24 bg-slate-900/30 border-y border-white/5">
            <div className="container mx-auto px-6">
                <div className="text-center mb-16">
                    <h2 className="text-4xl font-bold mb-4">
                        What Makes Zoro's<br />Security Integrations Different?
                    </h2>
                    <p className="text-slate-400 text-lg max-w-3xl mx-auto">
                        Every integration is built-in, fully featured, and configured by the agent itself.
                        No professional services. No playbook scripting. Just connect and go.
                    </p>
                </div>

                <div className="max-w-6xl mx-auto grid lg:grid-cols-2 gap-12 items-start">
                    {/* Left: Selectable Categories */}
                    <div className="space-y-2">
                        {categories.map((cat, i) => (
                            <button
                                key={cat.title}
                                onClick={() => setActiveCategory(i)}
                                className={`w-full text-left rounded-2xl p-6 transition-all duration-300 ${activeCategory === i
                                    ? 'bg-slate-800/80 border border-primary-500/30 shadow-lg shadow-primary-500/5'
                                    : 'bg-transparent border border-transparent hover:bg-slate-800/40'
                                    }`}
                            >
                                <h3 className={`text-xl font-bold mb-2 transition-colors ${activeCategory === i ? 'text-primary-400' : 'text-slate-400'
                                    }`}>
                                    {cat.title}
                                </h3>
                                {activeCategory === i && (
                                    <p className="text-sm text-slate-400 leading-relaxed animate-fade-in">
                                        {cat.desc}
                                    </p>
                                )}
                            </button>
                        ))}
                    </div>

                    {/* Right: Visual mockup + bullets */}
                    <div>
                        <div className="rounded-2xl bg-slate-900 border border-slate-700 overflow-hidden shadow-2xl">
                            <div className="flex items-center gap-2 px-4 py-3 bg-slate-800 border-b border-slate-700">
                                <div className="flex gap-1.5">
                                    <div className="w-2.5 h-2.5 rounded-full bg-red-500/60"></div>
                                    <div className="w-2.5 h-2.5 rounded-full bg-yellow-500/60"></div>
                                    <div className="w-2.5 h-2.5 rounded-full bg-green-500/60"></div>
                                </div>
                                <div className="flex-1 h-6 rounded bg-slate-700 flex items-center px-3">
                                    <span className="text-[10px] text-slate-500 font-mono">app.getzoro.ai/integrations</span>
                                </div>
                            </div>

                            <div className="p-6">
                                <div className="flex items-center gap-2 mb-4">
                                    <Plug className="w-4 h-4 text-primary-400" />
                                    <span className="text-sm font-semibold text-white">{active.mockTitle}</span>
                                    <span className="ml-auto px-2 py-0.5 rounded bg-emerald-500/20 text-emerald-400 text-[10px] font-medium">All Systems Go</span>
                                </div>

                                <div className="grid grid-cols-4 gap-2 mb-4">
                                    {active.tools.map((tool) => (
                                        <div key={tool} className="rounded-lg bg-slate-800 border border-slate-700 p-2 text-center hover:border-primary-500/40 transition-colors">
                                            <div className="text-[10px] font-medium text-slate-300 truncate">{tool}</div>
                                        </div>
                                    ))}
                                </div>

                                <div className="space-y-2">
                                    {active.mockItems.map((item) => (
                                        <div key={item.name} className="flex items-center justify-between px-3 py-2 rounded-lg bg-slate-800/50 border border-slate-700/50">
                                            <span className="text-xs text-slate-400">{item.name}</span>
                                            <span className={`flex items-center gap-1.5 text-[10px] font-medium ${colorMap[item.color]?.text || 'text-emerald-400'}`}>
                                                <span className={`w-1.5 h-1.5 rounded-full ${colorMap[item.color]?.bg || 'bg-emerald-400'}`}></span>
                                                {item.status}
                                            </span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>

                        <div className="mt-6 space-y-3">
                            {active.bullets.map((bullet, i) => (
                                <div key={i} className="flex items-start gap-3 text-sm">
                                    <CheckCircle className="w-4 h-4 text-primary-400 flex-shrink-0 mt-0.5" />
                                    <span className="text-slate-400">
                                        <span className="text-primary-400 font-semibold">{bullet.highlight}</span>
                                        {bullet.text}
                                    </span>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>

                <div className="mt-16 max-w-5xl mx-auto">
                    <div className="text-center mb-6">
                        <p className="text-sm text-slate-500 font-medium">Supported integrations include</p>
                    </div>
                    <div className="grid grid-cols-3 md:grid-cols-5 gap-3">
                        {[
                            'Splunk', 'CrowdStrike', 'Sentinel', 'Okta',
                            'ServiceNow', 'PagerDuty', 'SentinelOne', 'Jira',
                            'AWS', '+ More shipping monthly',
                        ].map((tool, i) => (
                            <div
                                key={i}
                                className={`rounded-lg ${tool.startsWith('+') ? 'bg-primary-600/20 border-primary-500/30' : 'bg-slate-900 border-slate-800'} border px-3 py-2 text-center hover:border-slate-600 transition-colors`}
                            >
                                <div className={`text-xs font-medium ${tool.startsWith('+') ? 'text-primary-400' : 'text-slate-400'}`}>{tool}</div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </section>
    );
}

/* ─── Security Operations Stack ────────────────────── */
function SecurityOpsStack() {
    const [activeCapability, setActiveCapability] = useState(0);

    const capabilities = [
        {
            id: 'cases',
            label: 'Cases',
            icon: FileText,
            tagline: 'Unified incident management',
            color: 'from-blue-500 to-cyan-500',
            accent: 'blue',
            description: 'Cases provide a single source of truth for every security incident. AI agents automatically populate cases with investigation findings, correlate related alerts, and maintain a complete audit trail — giving analysts full context without the manual grunt work.',
            features: [
                'Auto-populated investigation summaries',
                'Cross-alert correlation',
                'Evidence collection & chain of custody',
                'Team collaboration & handoffs',
            ],
            mockLines: [
                { icon: 'alert', text: 'Case SEC-2847 opened — brute force on prod-api-01', color: 'text-red-400' },
                { icon: 'link', text: 'Correlated: 3 related alerts from last 24h', color: 'text-blue-400' },
                { icon: 'check', text: 'Evidence: 47 failed logins, 2 geolocations, MFA bypass attempt', color: 'text-yellow-400' },
                { icon: 'check', text: 'Summary auto-generated. Assigned → SOC tier 2', color: 'text-emerald-400' },
            ],
        },
        {
            id: 'investigations',
            label: 'Investigations',
            icon: Search,
            tagline: 'AI-powered analysis',
            color: 'from-purple-500 to-violet-500',
            accent: 'purple',
            description: 'Zoro runs multi-step investigations across your entire security stack. It queries your SIEM, checks identity providers, pulls threat intel, and assembles a complete picture — reasoning through each step like a senior analyst.',
            features: [
                'Multi-tool cross-correlation in seconds',
                'Automated evidence gathering',
                'Reasoning traces for every decision',
                'Self-correcting when steps fail',
            ],
            mockLines: [
                { icon: 'brain', text: 'Planning: 5-step investigation across Splunk, Okta, CrowdStrike', color: 'text-purple-400' },
                { icon: 'search', text: 'Step 1: Querying Splunk for auth events (192.168.1.47)...', color: 'text-slate-400' },
                { icon: 'search', text: 'Step 2: Okta user enrichment — checking MFA status...', color: 'text-slate-400' },
                { icon: 'check', text: 'Investigation complete. Verdict: True Positive — compromised account', color: 'text-emerald-400' },
            ],
        },
        {
            id: 'detection',
            label: 'Detection',
            icon: Crosshair,
            tagline: 'Intelligent alert processing',
            color: 'from-amber-500 to-orange-500',
            accent: 'amber',
            description: 'Stop drowning in alerts. Zoro ingests every alert, deduplicates noise, enriches with context, and surfaces only what matters — drastically cutting mean-time-to-detect and eliminating analyst fatigue.',
            features: [
                'Real-time alert ingestion from all sources',
                'Automatic deduplication & grouping',
                'Context enrichment with threat intel',
                'Priority scoring based on risk and blast radius',
            ],
            mockLines: [
                { icon: 'alert', text: '3,247 alerts received in last 24h', color: 'text-amber-400' },
                { icon: 'check', text: '2,891 deduplicated / auto-resolved as known-good', color: 'text-emerald-400' },
                { icon: 'alert', text: '312 enriched with threat intel context', color: 'text-blue-400' },
                { icon: 'alert', text: '44 escalated to investigation pipeline → 3 true positives', color: 'text-red-400' },
            ],
        },
        {
            id: 'response',
            label: 'Response',
            icon: Zap,
            tagline: 'Automated remediation',
            color: 'from-emerald-500 to-teal-500',
            accent: 'emerald',
            description: 'When a threat is confirmed, Zoro acts. Isolate endpoints, revoke sessions, block IPs, create tickets, and notify stakeholders — all within your approval policy. High-risk actions wait for human sign-off.',
            features: [
                'Configurable autonomous responses',
                'Human-in-the-loop approval for high-risk actions',
                'Ticket creation and Slack/Teams notifications',
                'Full remediation audit trail',
            ],
            mockLines: [
                { icon: 'check', text: 'Threat confirmed. Executing response plan...', color: 'text-emerald-400' },
                { icon: 'action', text: '→ CrowdStrike: Endpoint isolated (host-prod-47)', color: 'text-blue-400' },
                { icon: 'action', text: '→ Okta: User session revoked (jsmith@acme.com)', color: 'text-blue-400' },
                { icon: 'check', text: '→ Jira: Ticket SEC-2847 created, assigned to IR team', color: 'text-emerald-400' },
            ],
        },
        {
            id: 'hunting',
            label: 'Hunting',
            icon: Target,
            tagline: 'Proactive threat search',
            color: 'from-red-500 to-pink-500',
            accent: 'red',
            description: 'Don\'t wait for alerts. Zoro proactively hunts for threats based on your hypotheses, TTPs from MITRE ATT&CK, and emerging threat intelligence — surfacing hidden risks before they become incidents.',
            features: [
                'Scheduled autonomous threat hunts',
                'MITRE ATT&CK technique mapping',
                'Behavioral anomaly detection',
                'Hunt findings auto-escalated to cases',
            ],
            mockLines: [
                { icon: 'search', text: 'Autonomous Hunt: Lateral movement detection (T1021)', color: 'text-red-400' },
                { icon: 'search', text: 'Scanning: 14 days of auth logs across 3 domains...', color: 'text-slate-400' },
                { icon: 'alert', text: 'Finding: Unusual RDP from service account → staging server', color: 'text-yellow-400' },
                { icon: 'check', text: 'Auto-escalated to Case SEC-2851 for investigation', color: 'text-emerald-400' },
            ],
        },
        {
            id: 'insights',
            label: 'Enterprise Insights',
            icon: BookOpen,
            tagline: 'Leverage tribal knowledge',
            color: 'from-cyan-500 to-blue-500',
            accent: 'cyan',
            description: 'Your best analysts\' knowledge shouldn\'t walk out the door. Zoro absorbs your SOPs, IR playbooks, escalation paths, and org context — compounding institutional knowledge that makes every investigation smarter.',
            features: [
                'Upload SOPs, runbooks, and IR plans',
                'Org-aware escalation paths',
                'Continuous learning from resolved incidents',
                'Knowledge persists across team turnover',
            ],
            mockLines: [
                { icon: 'brain', text: 'Knowledge base: 47 SOPs, 12 runbooks, 8 IR plans loaded', color: 'text-cyan-400' },
                { icon: 'check', text: 'Applied: "Acme phishing response SOP v3.2" to Case SEC-2844', color: 'text-emerald-400' },
                { icon: 'brain', text: 'Learning: Updated false-positive patterns from 23 resolved cases', color: 'text-purple-400' },
                { icon: 'check', text: 'Insight: Brute force attempts 3x higher on Fridays → adjusted schedule', color: 'text-blue-400' },
            ],
        },
    ];

    const active = capabilities[activeCapability];
    const ActiveIcon = active.icon;

    const iconForType = (type: string) => {
        switch (type) {
            case 'alert': return <AlertTriangle className="w-3 h-3 flex-shrink-0" />;
            case 'check': return <CheckCircle className="w-3 h-3 flex-shrink-0" />;
            case 'brain': return <Brain className="w-3 h-3 flex-shrink-0" />;
            case 'search': return <Search className="w-3 h-3 flex-shrink-0" />;
            case 'action': return <Zap className="w-3 h-3 flex-shrink-0" />;
            case 'link': return <Network className="w-3 h-3 flex-shrink-0" />;
            default: return <Activity className="w-3 h-3 flex-shrink-0" />;
        }
    };

    return (
        <section className="py-24 bg-slate-950 border-y border-white/5 relative overflow-hidden">
            {/* Background glow */}
            <div className="hidden md:block absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[400px] bg-primary-500/5 rounded-full blur-[60px]"></div>

            <div className="container mx-auto px-6 relative z-10">
                <div className="text-center mb-16">
                    <p className="text-sm text-primary-400 font-semibold uppercase tracking-widest mb-4">The Platform</p>
                    <h2 className="text-4xl lg:text-5xl font-bold mb-6">
                        The Complete Security<br />Operations Stack
                    </h2>
                    <p className="text-slate-400 text-lg max-w-3xl mx-auto">
                        From ingestion to response, Zoro handles the full lifecycle of security operations —
                        with AI agents at every layer.
                    </p>
                </div>

                {/* Capability Pills */}
                <div className="flex flex-wrap justify-center gap-2 mb-12 max-w-4xl mx-auto">
                    {capabilities.map((cap, i) => {
                        const Icon = cap.icon;
                        const isActive = activeCapability === i;
                        return (
                            <button
                                key={cap.id}
                                onClick={() => setActiveCapability(i)}
                                className={`group flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium transition-all duration-300 ${isActive
                                    ? `bg-gradient-to-r ${cap.color} text-white shadow-lg shadow-primary-500/10`
                                    : 'bg-slate-900 border border-slate-800 text-slate-400 hover:text-white hover:border-slate-600'
                                    }`}
                            >
                                <Icon className="w-4 h-4" />
                                <span className="hidden sm:inline">{cap.label}</span>
                                <span className="sm:hidden">{cap.label.split(' ')[0]}</span>
                            </button>
                        );
                    })}
                </div>

                {/* Active Capability Detail */}
                <div className="max-w-6xl mx-auto grid lg:grid-cols-2 gap-8 items-start">
                    {/* Left: Description + Features */}
                    <div className="order-2 lg:order-1">
                        <div className="flex items-center gap-3 mb-4">
                            <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${active.color} flex items-center justify-center shadow-lg`}>
                                <ActiveIcon className="w-5 h-5 text-white" />
                            </div>
                            <div>
                                <h3 className="text-2xl font-bold text-white">{active.label}</h3>
                                <p className="text-sm text-slate-500">{active.tagline}</p>
                            </div>
                        </div>

                        <p className="text-slate-400 leading-relaxed mb-6">
                            {active.description}
                        </p>

                        <div className="space-y-3">
                            {active.features.map((feature, i) => (
                                <div key={i} className="flex items-start gap-3">
                                    <CheckCircle className="w-4 h-4 text-primary-400 flex-shrink-0 mt-0.5" />
                                    <span className="text-sm text-slate-300">{feature}</span>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Right: Mock Terminal */}
                    <div className="order-1 lg:order-2">
                        <div className="rounded-2xl bg-slate-900 border border-slate-700 overflow-hidden shadow-2xl">
                            {/* Terminal header */}
                            <div className="flex items-center gap-2 px-4 py-3 bg-slate-800 border-b border-slate-700">
                                <div className="flex gap-1.5">
                                    <div className="w-2.5 h-2.5 rounded-full bg-red-500/60"></div>
                                    <div className="w-2.5 h-2.5 rounded-full bg-yellow-500/60"></div>
                                    <div className="w-2.5 h-2.5 rounded-full bg-green-500/60"></div>
                                </div>
                                <div className="flex-1 h-6 rounded bg-slate-700 flex items-center px-3">
                                    <span className="text-[10px] text-slate-500 font-mono">app.getzoro.ai/{active.id}</span>
                                </div>
                            </div>

                            {/* Terminal content */}
                            <div className="p-6">
                                <div className="flex items-center gap-2 mb-4">
                                    <ActiveIcon className="w-4 h-4 text-primary-400" />
                                    <span className="text-sm font-semibold text-white">{active.label}</span>
                                    <span className="ml-auto px-2 py-0.5 rounded bg-emerald-500/20 text-emerald-400 text-[10px] font-medium">Active</span>
                                </div>

                                <div className="space-y-3 font-mono text-xs">
                                    {active.mockLines.map((line, i) => (
                                        <div
                                            key={`${active.id}-${i}`}
                                            className={`flex items-start gap-2 ${line.color}`}
                                            style={{ animationDelay: `${i * 150}ms` }}
                                        >
                                            {iconForType(line.icon)}
                                            <span className="leading-relaxed">{line.text}</span>
                                        </div>
                                    ))}
                                </div>

                                {/* Stats bar */}
                                <div className="mt-6 pt-4 border-t border-slate-700/50 grid grid-cols-3 gap-4">
                                    <div className="text-center">
                                        <div className="text-lg font-bold text-white">24/7</div>
                                        <div className="text-[10px] text-slate-500">Coverage</div>
                                    </div>
                                    <div className="text-center">
                                        <div className="text-lg font-bold text-emerald-400">&lt;2 min</div>
                                        <div className="text-[10px] text-slate-500">Avg Response</div>
                                    </div>
                                    <div className="text-center">
                                        <div className="text-lg font-bold text-primary-400">80%</div>
                                        <div className="text-[10px] text-slate-500">Auto-Resolved</div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
}

/* ─── Pricing + ROI Section ────────────────────────── */
function PricingSection({ onGetStarted }: { onGetStarted: () => void }) {
    const [analysts, setAnalysts] = useState(5);
    const [investigations, setInvestigations] = useState(1000);
    const [mttr, setMttr] = useState(45);
    const [selectedTier, setSelectedTier] = useState<'starter' | 'pro'>('starter');

    const analystCost = 120000;
    const currentCost = analysts * analystCost;
    const tierPrice = selectedTier === 'starter' ? 990 : 2490;
    const tierMaxInvestigations = selectedTier === 'starter' ? 2000 : 8000;
    const effectiveInvestigations = Math.min(investigations, tierMaxInvestigations);
    const needsUpgrade = selectedTier === 'starter' && investigations > 2000;
    const needsEnterprise = investigations > 8000;
    const zoroCostAnnual = tierPrice * 12;
    const investigationsHandledByZoro = Math.round(effectiveInvestigations * 0.80);
    const reducedAnalysts = Math.max(1, Math.round(analysts * 0.4));
    const newMttr = Math.max(2, Math.round(mttr * 0.12));
    const totalSavings = currentCost - (reducedAnalysts * analystCost + zoroCostAnnual);

    const tiers = [
        {
            name: 'Starter',
            price: '$990',
            period: '/mo',
            alerts: 'Up to 2,000 investigations/mo',
            highlight: false,
            features: [
                'Full autonomous agent with self-correction',
                'All integrations included',
                'Unlimited users',
                'Dedicated data isolation',
                'Chat + Deploy as Task',
                'Email support',
            ],
            cta: 'Start a Guided Pilot',
        },
        {
            name: 'Pro',
            price: '$2,490',
            period: '/mo',
            alerts: 'Up to 8,000 investigations/mo',
            highlight: true,
            features: [
                'Full autonomous agent with self-correction',
                'All integrations included',
                'Unlimited users',
                'Dedicated data isolation',
                'Chat + Deploy as Task',
                'Priority support + 99.5% SLA',
            ],
            cta: 'Start a Guided Pilot',
        },
        {
            name: 'Enterprise',
            price: 'Custom',
            period: '',
            alerts: 'Custom',
            highlight: false,
            features: [
                'Full autonomous agent with self-correction',
                'All integrations + custom connectors',
                'Unlimited users',
                'Dedicated data isolation',
                'Dedicated CSM + 99.9% SLA',
                'Cloud or self-hosted deployment',
                'Custom alert routing rules',
            ],
            cta: 'Contact Sales',
        },
        {
            name: 'MSSP',
            price: 'Custom',
            period: '',
            alerts: 'Unlimited investigations',
            highlight: false,
            features: [
                'Full autonomous agent with self-correction',
                'All integrations + custom connectors',
                'Full multi-tenant isolation',
                'Per-client dashboards & branding',
                'Dedicated CSM + TAM',
                '99.99% SLA',
                'Dedicated training & enablement',
            ],
            cta: 'Contact Sales',
        },
    ];

    return (
        <section id="pricing" className="py-24">
            <div className="container mx-auto px-6">
                <div className="text-center mb-4">
                    <h2 className="text-4xl font-bold mb-4">Simple, Transparent Pricing</h2>
                    <p className="text-slate-400 text-lg max-w-2xl mx-auto">
                        Every plan includes dedicated data isolation. Your data is yours—we never see it.
                    </p>
                </div>

                {/* Data Isolation Badge */}
                <div className="flex justify-center mb-12">
                    <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 text-sm font-medium">
                        <Lock className="w-4 h-4" />
                        Dedicated data isolation on every plan—your data never leaves your environment
                    </div>
                </div>

                {/* Pricing Cards */}
                <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-6xl mx-auto mb-24">
                    {tiers.map((tier) => (
                        <div
                            key={tier.name}
                            className={`rounded-2xl p-6 flex flex-col ${tier.highlight
                                ? 'bg-gradient-to-b from-primary-900/40 to-slate-900 border-2 border-primary-500/40 shadow-xl shadow-primary-500/10 relative'
                                : 'bg-slate-900 border border-slate-800'
                                }`}
                        >
                            {tier.highlight && (
                                <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-0.5 rounded-full bg-primary-500 text-white text-xs font-bold">
                                    Most Popular
                                </div>
                            )}
                            <div className="mb-4">
                                <h3 className="text-lg font-bold text-white">{tier.name}</h3>
                                <p className="text-xs text-slate-500 mt-1">{tier.alerts}</p>
                            </div>
                            <div className="mb-6">
                                <span className="text-3xl font-bold text-white">{tier.price}</span>
                                <span className="text-slate-500">{tier.period}</span>
                            </div>
                            <ul className="space-y-2.5 mb-8 flex-1">
                                {tier.features.map((f, i) => (
                                    <li key={i} className="flex items-start gap-2 text-sm text-slate-400">
                                        <CheckCircle className="w-4 h-4 text-primary-400 flex-shrink-0 mt-0.5" />
                                        <span>{f}</span>
                                    </li>
                                ))}
                            </ul>
                            <button
                                onClick={onGetStarted}
                                className={`w-full py-2.5 rounded-xl font-medium text-sm transition-all ${tier.highlight
                                    ? 'bg-primary-600 hover:bg-primary-500 text-white shadow-lg shadow-primary-500/25'
                                    : 'bg-slate-800 hover:bg-slate-700 text-white border border-slate-700'
                                    }`}
                            >
                                {tier.cta}
                            </button>
                        </div>
                    ))}
                </div>

                {/* ROI Calculator */}
                <div className="text-center mb-16">
                    <h2 className="text-3xl font-bold mb-4">Calculate Your ROI</h2>
                    <p className="text-slate-400 max-w-2xl mx-auto">
                        Select your plan and see how much Zoro saves your team.
                    </p>
                </div>

                <div className="max-w-5xl mx-auto grid lg:grid-cols-2 gap-8">
                    {/* Inputs */}
                    <div className="rounded-2xl bg-slate-900 border border-slate-800 p-8">
                        {/* Tier Selector */}
                        <div className="mb-6">
                            <label className="text-sm text-slate-400 mb-2 block">Select Plan</label>
                            <div className="grid grid-cols-2 gap-2 p-1 rounded-xl bg-slate-800">
                                <button
                                    onClick={() => setSelectedTier('starter')}
                                    className={`py-2 rounded-lg text-sm font-medium transition-all ${selectedTier === 'starter'
                                        ? 'bg-primary-600 text-white shadow-lg'
                                        : 'text-slate-400 hover:text-white'
                                        }`}
                                >
                                    Starter — $990/mo
                                </button>
                                <button
                                    onClick={() => setSelectedTier('pro')}
                                    className={`py-2 rounded-lg text-sm font-medium transition-all ${selectedTier === 'pro'
                                        ? 'bg-primary-600 text-white shadow-lg'
                                        : 'text-slate-400 hover:text-white'
                                        }`}
                                >
                                    Pro — $2,490/mo
                                </button>
                            </div>
                            <p className="text-xs text-slate-600 mt-2">Enterprise &amp; MSSP — <button onClick={onGetStarted} className="text-primary-400 hover:underline">Contact Sales</button></p>
                        </div>

                        <h3 className="text-lg font-bold mb-6 flex items-center gap-2">
                            <Gauge className="w-5 h-5 text-primary-400" />
                            Your Current Setup
                        </h3>

                        <div className="space-y-6">
                            <div>
                                <div className="flex justify-between mb-2">
                                    <label className="text-sm text-slate-400">Security Analysts</label>
                                    <span className="text-sm font-mono text-white">{analysts}</span>
                                </div>
                                <div className="flex items-center gap-3">
                                    <button onClick={() => setAnalysts(Math.max(1, analysts - 1))} className="w-8 h-8 rounded-lg bg-slate-800 border border-slate-700 flex items-center justify-center text-slate-400 hover:text-white transition-colors">
                                        <Minus className="w-4 h-4" />
                                    </button>
                                    <input type="range" min="1" max="50" value={analysts} onChange={(e) => setAnalysts(parseInt(e.target.value))} className="flex-1 accent-primary-500" />
                                    <button onClick={() => setAnalysts(Math.min(50, analysts + 1))} className="w-8 h-8 rounded-lg bg-slate-800 border border-slate-700 flex items-center justify-center text-slate-400 hover:text-white transition-colors">
                                        <Plus className="w-4 h-4" />
                                    </button>
                                </div>
                            </div>

                            <div>
                                <div className="flex justify-between mb-2">
                                    <label className="text-sm text-slate-400">Investigations Per Month</label>
                                    <span className="text-sm font-mono text-white">{investigations.toLocaleString()}</span>
                                </div>
                                <div className="flex items-center gap-3">
                                    <button onClick={() => setInvestigations(Math.max(100, investigations - 200))} className="w-8 h-8 rounded-lg bg-slate-800 border border-slate-700 flex items-center justify-center text-slate-400 hover:text-white transition-colors">
                                        <Minus className="w-4 h-4" />
                                    </button>
                                    <input type="range" min="100" max="10000" step="100" value={investigations} onChange={(e) => setInvestigations(parseInt(e.target.value))} className="flex-1 accent-primary-500" />
                                    <button onClick={() => setInvestigations(Math.min(10000, investigations + 200))} className="w-8 h-8 rounded-lg bg-slate-800 border border-slate-700 flex items-center justify-center text-slate-400 hover:text-white transition-colors">
                                        <Plus className="w-4 h-4" />
                                    </button>
                                </div>
                                {needsUpgrade && (
                                    <p className="text-xs text-yellow-400 mt-2 flex items-center gap-1">
                                        <AlertTriangle className="w-3 h-3" />
                                        {investigations.toLocaleString()} investigations/mo exceeds Starter (2,000). Consider upgrading to Pro.
                                    </p>
                                )}
                                {needsEnterprise && (
                                    <p className="text-xs text-yellow-400 mt-2 flex items-center gap-1">
                                        <AlertTriangle className="w-3 h-3" />
                                        {investigations.toLocaleString()} investigations/mo requires Enterprise or MSSP. <button onClick={onGetStarted} className="text-primary-400 hover:underline ml-1">Contact Sales</button>
                                    </p>
                                )}
                            </div>

                            <div>
                                <div className="flex justify-between mb-2">
                                    <label className="text-sm text-slate-400">Avg MTTR (minutes)</label>
                                    <span className="text-sm font-mono text-white">{mttr} min</span>
                                </div>
                                <div className="flex items-center gap-3">
                                    <button onClick={() => setMttr(Math.max(5, mttr - 5))} className="w-8 h-8 rounded-lg bg-slate-800 border border-slate-700 flex items-center justify-center text-slate-400 hover:text-white transition-colors">
                                        <Minus className="w-4 h-4" />
                                    </button>
                                    <input type="range" min="5" max="120" step="5" value={mttr} onChange={(e) => setMttr(parseInt(e.target.value))} className="flex-1 accent-primary-500" />
                                    <button onClick={() => setMttr(Math.min(120, mttr + 5))} className="w-8 h-8 rounded-lg bg-slate-800 border border-slate-700 flex items-center justify-center text-slate-400 hover:text-white transition-colors">
                                        <Plus className="w-4 h-4" />
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Results */}
                    <div className="rounded-2xl bg-gradient-to-br from-primary-900/30 to-slate-900 border border-primary-500/20 p-8">
                        <h3 className="text-lg font-bold mb-2 flex items-center gap-2">
                            <DollarSign className="w-5 h-5 text-emerald-400" />
                            With Zoro {selectedTier === 'starter' ? 'Starter' : 'Pro'}
                        </h3>
                        <p className="text-sm text-slate-500 mb-6">${tierPrice.toLocaleString()}/mo · Up to {tierMaxInvestigations.toLocaleString()} investigations/mo</p>

                        <div className="space-y-4 mb-6">
                            <div className="flex justify-between items-center p-3 rounded-xl bg-slate-800/50 border border-slate-700/50">
                                <div className="flex items-center gap-2 text-sm text-slate-400">
                                    <Users className="w-4 h-4" /> Analysts needed
                                </div>
                                <div className="text-right">
                                    <span className="text-slate-500 line-through text-sm mr-2">{analysts}</span>
                                    <span className="text-white font-bold">{reducedAnalysts}</span>
                                </div>
                            </div>

                            <div className="flex justify-between items-center p-3 rounded-xl bg-slate-800/50 border border-slate-700/50">
                                <div className="flex items-center gap-2 text-sm text-slate-400">
                                    <Bot className="w-4 h-4" /> Investigations auto-resolved
                                </div>
                                <div className="text-right">
                                    <span className="text-emerald-400 font-bold">{investigationsHandledByZoro.toLocaleString()}</span>
                                    <span className="text-slate-500 text-sm ml-1">/ mo</span>
                                </div>
                            </div>

                            <div className="flex justify-between items-center p-3 rounded-xl bg-slate-800/50 border border-slate-700/50">
                                <div className="flex items-center gap-2 text-sm text-slate-400">
                                    <Clock className="w-4 h-4" /> Mean time to respond
                                </div>
                                <div className="text-right">
                                    <span className="text-slate-500 line-through text-sm mr-2">{mttr} min</span>
                                    <span className="text-emerald-400 font-bold">{newMttr} min</span>
                                </div>
                            </div>

                            <div className="flex justify-between items-center p-3 rounded-xl bg-slate-800/50 border border-slate-700/50">
                                <div className="flex items-center gap-2 text-sm text-slate-400">
                                    <DollarSign className="w-4 h-4" /> Annual platform cost
                                </div>
                                <span className="text-white font-bold">${zoroCostAnnual.toLocaleString()}</span>
                            </div>
                        </div>

                        {needsEnterprise ? (
                            <div className="rounded-xl bg-yellow-500/10 border border-yellow-500/30 p-4 text-center">
                                <p className="text-xs text-yellow-400 mb-2">Your investigation volume requires a custom plan</p>
                                <button onClick={onGetStarted} className="px-4 py-2 rounded-lg bg-primary-600 hover:bg-primary-500 text-white text-sm font-medium transition-all">
                                    Contact Sales for Enterprise Pricing
                                </button>
                            </div>
                        ) : totalSavings > 0 ? (
                            <div className="rounded-xl bg-emerald-500/10 border border-emerald-500/30 p-4 text-center">
                                <p className="text-xs text-emerald-400 mb-1">Estimated Annual Savings</p>
                                <p className="text-3xl font-bold text-emerald-400">${totalSavings.toLocaleString()}</p>
                            </div>
                        ) : (
                            <div className="rounded-xl bg-primary-500/10 border border-primary-500/30 p-4 text-center">
                                <p className="text-xs text-primary-400 mb-1">ROI from faster response</p>
                                <p className="text-xl font-bold text-primary-400">{Math.round((1 - newMttr / mttr) * 100)}% faster MTTR</p>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </section>
    );
}

/* ─── Landing Page ────────────────────────────────── */
export function LandingPage({ onGetStarted: _onGetStarted }: LandingPageProps) {
    const [showDemoModal, setShowDemoModal] = useState(false);
    const [showInteractiveDemo, setShowInteractiveDemo] = useState(false);

    const openModal = () => setShowDemoModal(true);

    return (
        <div className="min-h-screen bg-slate-950 text-white overflow-x-hidden selection:bg-primary-500/30">
            {/* Demo Request Modal */}
            <DemoRequestModal
                isOpen={showDemoModal}
                onClose={() => setShowDemoModal(false)}
            />
            <InteractiveDemo
                isOpen={showInteractiveDemo}
                onClose={() => setShowInteractiveDemo(false)}
            />
            {/* Nav */}
            <nav className="fixed top-0 w-full z-50 border-b border-white/5 bg-slate-950/95 nav-fixed">
                <div className="container mx-auto px-6 h-20 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <img src={logoImage} alt="Zoro" className="w-8 h-8 rounded-lg" />
                        <span className="font-bold text-xl tracking-tight">Zoro</span>
                    </div>
                    <div className="hidden md:flex items-center gap-8 text-sm font-medium text-slate-400">
                        <a href="#platform" className="hover:text-white transition-colors">Platform</a>
                        <a href="#how-it-works" className="hover:text-white transition-colors">How It Works</a>
                        <a href="#integrations" className="hover:text-white transition-colors">Integrations</a>
                        <a href="#pricing" className="hover:text-white transition-colors">Pricing</a>
                        <a href="#security" className="hover:text-white transition-colors">Security</a>
                    </div>
                    <div className="flex items-center gap-3">
                        <button
                            onClick={() => setShowInteractiveDemo(true)}
                            className="hidden sm:inline-flex px-4 py-2 rounded-full bg-transparent hover:bg-white/5 border border-white/10 text-sm font-medium text-slate-300 transition-all"
                        >
                            See Zoro Investigate
                        </button>
                        <button
                            onClick={openModal}
                            className="px-5 py-2 rounded-full bg-primary-600 hover:bg-primary-500 text-white text-sm font-medium transition-all shadow-lg shadow-primary-500/20"
                        >
                            Start a Guided Pilot
                        </button>
                    </div>
                </div>
            </nav>

            {/* S1: Hero */}
            <header className="relative pt-32 pb-20 lg:pt-48 lg:pb-32 overflow-hidden">
                <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[600px] h-[400px] bg-primary-500/15 rounded-full blur-[40px] md:blur-[80px] -z-10 opacity-50"></div>
                <div className="hidden md:block absolute bottom-0 right-0 w-[500px] h-[400px] bg-blue-600/10 rounded-full blur-[80px] -z-10 opacity-30"></div>
                <div className="hidden md:block absolute top-1/4 left-10 w-72 h-72 bg-purple-600/10 rounded-full blur-[60px] -z-10"></div>

                <div className="container mx-auto px-6 text-center z-10 relative">
                    <p className="text-sm text-slate-500 font-medium mb-6 animate-fade-in">
                        Not another "AI SOC tool." Not a copilot. Not a chatbot bolted onto your SIEM.
                    </p>
                    <h1 className="text-5xl lg:text-7xl font-bold tracking-tight mb-8 leading-tight animate-slide-up">
                        Your Newest Security Analyst<br />
                        <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary-400 via-purple-400 to-blue-400">
                            Starts Today.
                        </span>
                    </h1>

                    <p className="text-xl text-slate-400 max-w-3xl mx-auto mb-12 animate-slide-up [animation-delay:0.2s]">
                        Zoro is a fully autonomous security agent that investigates every alert,
                        writes its own playbooks, and self-corrects when things fail
                        — <span className="text-white font-medium">while your team sleeps</span>.
                    </p>

                    <div className="flex flex-col sm:flex-row items-center justify-center gap-4 animate-slide-up [animation-delay:0.4s]">
                        <button
                            onClick={() => setShowInteractiveDemo(true)}
                            className="px-8 py-4 rounded-xl bg-primary-600 hover:bg-primary-500 text-white font-bold text-lg shadow-lg shadow-primary-500/25 transition-all hover:scale-105 flex items-center gap-3"
                        >
                            <Eye className="w-5 h-5" /> See Zoro Investigate
                        </button>
                        <button
                            onClick={openModal}
                            className="px-8 py-4 rounded-xl bg-slate-900 hover:bg-slate-800 border border-slate-700 text-white font-medium transition-all flex items-center gap-3"
                        >
                            Start a Guided Pilot <ArrowRight className="w-5 h-5" />
                        </button>
                    </div>

                    {/* Trust Signals */}
                    <div className="mt-12 flex flex-wrap items-center justify-center gap-6 text-slate-500 text-xs font-medium animate-fade-in [animation-delay:0.6s]">
                        <span className="flex items-center gap-2"><Zap className="w-3.5 h-3.5" /> Deploys in 5 minutes</span>
                        <span className="text-slate-700">·</span>
                        <span className="flex items-center gap-2"><Plug className="w-3.5 h-3.5" /> Works with your existing tools</span>
                        <span className="text-slate-700">·</span>
                        <span className="flex items-center gap-2"><Lock className="w-3.5 h-3.5" /> Dedicated data isolation on every plan</span>
                    </div>

                    {/* Social proof line */}
                    <p className="mt-6 text-sm text-slate-600 animate-fade-in [animation-delay:0.8s]">
                        Deployed by government agencies, MSPs, and enterprise security teams.
                    </p>
                </div>
            </header>

            {/* S2: Meet Zoro */}
            <section className="py-24 bg-slate-900/50 border-y border-white/5">
                <div className="container mx-auto px-6">
                    <div className="text-center mb-16">
                        <h2 className="text-4xl font-bold mb-4">Meet Your Newest Security Analyst</h2>
                    </div>

                    <div className="max-w-5xl mx-auto grid lg:grid-cols-2 gap-12 items-center">
                        {/* Employee Card */}
                        <div className="rounded-3xl bg-gradient-to-br from-slate-800/80 to-slate-900 border border-slate-700 p-8 shadow-2xl relative overflow-hidden">
                            <div className="hidden md:block absolute top-0 right-0 w-48 h-48 bg-primary-500/10 rounded-full blur-[40px]"></div>
                            <div className="relative">
                                <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-primary-500 to-primary-700 flex items-center justify-center mb-6 shadow-lg shadow-primary-500/25">
                                    <Bot className="w-10 h-10 text-white" />
                                </div>
                                <h3 className="text-2xl font-bold text-white mb-1">ZORO</h3>
                                <p className="text-sm text-primary-400 font-medium mb-6">Autonomous Security Analyst</p>

                                <div className="space-y-3 mb-8">
                                    <div className="flex items-center justify-between py-2 border-b border-slate-700/50">
                                        <span className="text-xs text-slate-500 uppercase tracking-wider">Status</span>
                                        <span className="text-sm text-emerald-400 font-medium flex items-center gap-1.5">
                                            <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse"></span> Active 24/7
                                        </span>
                                    </div>
                                    <div className="flex items-center justify-between py-2 border-b border-slate-700/50">
                                        <span className="text-xs text-slate-500 uppercase tracking-wider">Clearance</span>
                                        <span className="text-sm text-slate-300">Configurable</span>
                                    </div>
                                    <div className="flex items-center justify-between py-2 border-b border-slate-700/50">
                                        <span className="text-xs text-slate-500 uppercase tracking-wider">Tools</span>
                                        <span className="text-sm text-slate-300">Splunk, CrowdStrike, Okta, +6</span>
                                    </div>
                                    <div className="flex items-center justify-between py-2">
                                        <span className="text-xs text-slate-500 uppercase tracking-wider">First Day</span>
                                        <span className="text-sm text-primary-400 font-medium">Today</span>
                                    </div>
                                </div>

                                <button onClick={openModal} className="w-full py-3 rounded-xl bg-primary-600 hover:bg-primary-500 text-white font-medium transition-all shadow-lg shadow-primary-500/25">
                                    Start a Guided Pilot
                                </button>
                            </div>
                        </div>

                        {/* Capability bullets */}
                        <div className="space-y-8">
                            {[
                                { title: 'ZORO LEARNS YOUR ENVIRONMENT.', body: 'Your tools, your escalation paths, your org structure. It doesn\'t run generic runbooks — it builds its own understanding of how your team operates.' },
                                { title: 'TRAIN ZORO LIKE A NEW HIRE.', body: 'Feed Zoro your IR playbooks, compliance docs, runbooks, org charts. Your institutional knowledge compounds inside Zoro instead of walking out the door when analysts leave.' },
                                { title: 'ZORO REASONS THROUGH PROBLEMS.', body: 'When an alert fires, Zoro plans a multi-step investigation, pulls context from your SIEM, checks threat intel, verifies identity, and takes action — the way a senior analyst would.' },
                                { title: 'ZORO SELF-CORRECTS.', body: 'When a step fails, it replans. No broken pipelines. No 3am pages about your automation being down.' },
                                { title: 'ZORO REMEMBERS.', body: 'Incidents, patterns, false positives, your org\'s quirks. Context carries across investigations — just like a real teammate.' },
                            ].map((item) => (
                                <div key={item.title}>
                                    <h4 className="text-sm font-bold text-primary-400 uppercase tracking-wider mb-2">{item.title}</h4>
                                    <p className="text-sm text-slate-400 leading-relaxed">{item.body}</p>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            {/* S3: The Problem */}
            <section className="py-24 bg-slate-950 border-y border-white/5">
                <div className="container mx-auto px-6">
                    <div className="text-center mb-16">
                        <h2 className="text-4xl font-bold mb-4">The Real Problem Isn't Your Tools. <span className="text-transparent bg-clip-text bg-gradient-to-r from-red-400 to-amber-400">It's Math.</span></h2>
                        <p className="text-slate-400 text-lg max-w-3xl mx-auto">
                            Your team gets thousands of alerts a day. You have a handful of analysts.
                            The math doesn't work — so most alerts get ignored, auto-closed, or triaged by keyword.
                            Threats slip through.
                        </p>
                    </div>

                    <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 max-w-5xl mx-auto mb-16">
                        {[
                            { stat: '3.5M', title: 'Open cybersecurity positions globally.', body: 'You can\'t hire fast enough.' },
                            { stat: '18–24 mo', title: 'Avg analyst tenure before burnout.', body: 'Your best people keep leaving.' },
                            { stat: '6–12 mo', title: 'Avg deploy time for "AI SOC" tools.', body: 'They still need constant tuning.' },
                            { stat: '80%+', title: 'Alerts that go uninvestigated.', body: 'Not enough humans.' },
                        ].map((card) => (
                            <div key={card.stat} className="rounded-2xl bg-slate-900 border border-slate-800 p-6 text-center hover:border-red-500/30 transition-all">
                                <div className="text-3xl font-bold text-red-400 mb-3">{card.stat}</div>
                                <p className="text-sm text-white font-medium mb-1">{card.title}</p>
                                <p className="text-xs text-slate-500">{card.body}</p>
                            </div>
                        ))}
                    </div>

                    <p className="text-center text-xl font-bold text-slate-300 max-w-2xl mx-auto">
                        The answer isn't another tool. It's another analyst. <span className="text-primary-400">One that actually works.</span>
                    </p>
                </div>
            </section>

            {/* S3.5: Differentiator */}
            <section className="py-24">
                <div className="container mx-auto px-6">
                    <div className="text-center mb-16">
                        <h2 className="text-4xl font-bold mb-4">Stop. This Isn't Another "AI SOC Tool."</h2>
                    </div>

                    <div className="max-w-5xl mx-auto grid lg:grid-cols-2 gap-6">
                        {/* What everyone else builds — muted */}
                        <div className="rounded-2xl bg-slate-900/50 border border-slate-800 p-8 opacity-70">
                            <h3 className="text-lg font-bold text-slate-500 uppercase tracking-wider mb-6">What "AI for Security" Usually Means</h3>
                            <ul className="space-y-3 text-sm text-slate-500">
                                <li className="flex items-start gap-2"><span className="text-slate-600 mt-0.5">→</span> A chatbot that sits next to your analyst</li>
                                <li className="flex items-start gap-2"><span className="text-slate-600 mt-0.5">→</span> An LLM that suggests next steps (you still execute)</li>
                                <li className="flex items-start gap-2"><span className="text-slate-600 mt-0.5">→</span> A playbook engine with AI-generated templates</li>
                                <li className="flex items-start gap-2"><span className="text-slate-600 mt-0.5">→</span> A summarizer that writes up what already happened</li>
                                <li className="flex items-start gap-2"><span className="text-slate-600 mt-0.5">→</span> Requires your team to supervise every interaction</li>
                            </ul>
                            <div className="mt-6 pt-4 border-t border-slate-800">
                                <p className="text-sm text-slate-600 italic">Your analysts are still doing the same job. They just have a slightly faster assistant.</p>
                            </div>
                        </div>

                        {/* What Zoro is — bright */}
                        <div className="rounded-2xl bg-gradient-to-br from-primary-900/30 to-slate-900 border-2 border-primary-500/30 p-8 shadow-xl shadow-primary-500/5">
                            <h3 className="text-lg font-bold text-primary-400 uppercase tracking-wider mb-6">What Zoro Actually Is</h3>
                            <ul className="space-y-3 text-sm text-slate-300">
                                <li className="flex items-start gap-2"><span className="text-primary-400 mt-0.5">→</span> An autonomous agent that receives an alert, plans its own investigation, executes across your tools, and <span className="text-white font-medium">resolves the incident — start to finish</span></li>
                                <li className="flex items-start gap-2"><span className="text-primary-400 mt-0.5">→</span> Writes its own playbooks on the fly, not from templates</li>
                                <li className="flex items-start gap-2"><span className="text-primary-400 mt-0.5">→</span> Self-corrects when steps fail — no broken pipelines</li>
                                <li className="flex items-start gap-2"><span className="text-primary-400 mt-0.5">→</span> Learns from your docs, your IR plans, your org context</li>
                                <li className="flex items-start gap-2"><span className="text-primary-400 mt-0.5">→</span> Works while your team sleeps, and is ready to brief them in the morning</li>
                            </ul>
                            <div className="mt-6 pt-4 border-t border-primary-500/20">
                                <p className="text-sm text-primary-300 font-medium">Your analysts aren't doing the same job faster. They're doing a different, more strategic job entirely.</p>
                            </div>
                        </div>
                    </div>

                    <p className="text-center text-lg text-slate-400 mt-12 max-w-3xl mx-auto">
                        The industry is focused on making humans faster.
                        <span className="text-white font-bold"> Zoro is focused on making humans unnecessary for the work that shouldn't require humans in the first place.</span>
                    </p>
                </div>
            </section>

            {/* Security Operations Stack */}
            <SecurityOpsStack />

            {/* S4: How It Works */}
            <section id="platform" className="py-24">
                <div className="container mx-auto px-6">
                    <div className="text-center mb-16">
                        <h2 className="text-4xl font-bold mb-4">What Happens When an Alert Fires</h2>
                        <p className="text-slate-400 text-lg max-w-2xl mx-auto">
                            Every alert becomes a structured investigation. Zoro plans,
                            executes, self-corrects, and resolves — while you focus on what matters.
                        </p>
                    </div>

                    {/* Dashboard Preview - keep existing */}
                    <div className="relative rounded-3xl bg-gradient-to-br from-slate-800/50 to-slate-900 border border-slate-700 p-2 max-w-5xl mx-auto overflow-hidden group">
                        <div className="absolute inset-0 bg-grid-white/[0.02] bg-[size:20px_20px]"></div>
                        <div className="hidden md:block absolute top-0 right-0 w-96 h-96 bg-primary-500/10 rounded-full blur-[60px]"></div>
                        <div className="relative bg-slate-900 rounded-2xl p-8 border border-slate-800">
                            <div className="flex items-center gap-3 mb-6">
                                <div className="flex gap-1.5">
                                    <div className="w-3 h-3 rounded-full bg-red-500/80"></div>
                                    <div className="w-3 h-3 rounded-full bg-yellow-500/80"></div>
                                    <div className="w-3 h-3 rounded-full bg-green-500/80"></div>
                                </div>
                                <div className="flex-1 h-8 rounded-lg bg-slate-800 flex items-center px-4 text-xs text-slate-500 font-mono">
                                    app.getzoro.ai/command-center
                                </div>
                            </div>
                            <div className="grid grid-cols-3 gap-4 h-72">
                                <div className="col-span-2 rounded-xl bg-slate-800/50 border border-slate-700 p-4">
                                    <div className="flex items-center gap-2 mb-4">
                                        <Rocket className="w-4 h-4 text-primary-400" />
                                        <span className="text-sm font-medium">Autonomous Task Pipeline</span>
                                        <span className="ml-auto px-2 py-0.5 rounded bg-emerald-500/20 text-emerald-400 text-xs">Live</span>
                                    </div>
                                    <div className="space-y-2.5 text-xs text-slate-400 font-mono">
                                        <div className="flex items-center gap-2">
                                            <AlertTriangle className="w-3 h-3 text-red-400" />
                                            <span className="text-red-300">Alert: Brute force from 192.168.1.100</span>
                                        </div>
                                        <div className="flex items-center gap-2 pl-4">
                                            <Brain className="w-3 h-3 text-purple-400" />
                                            <span>Planner: Decomposed into 4 steps</span>
                                        </div>
                                        <div className="flex items-center gap-2 pl-4">
                                            <Activity className="w-3 h-3 text-blue-400" />
                                            <span>Step 1: splunk_search — querying failed logins...</span>
                                        </div>
                                        <div className="flex items-center gap-2 pl-4">
                                            <Eye className="w-3 h-3 text-yellow-400" />
                                            <span>Step 2: threat_intel_lookup — checking IP reputation...</span>
                                        </div>
                                        <div className="flex items-center gap-2 pl-4">
                                            <CheckCircle className="w-3 h-3 text-green-400" />
                                            <span>Step 3: okta_get_user — verified MFA status</span>
                                        </div>
                                        <div className="flex items-center gap-2 pl-4">
                                            <Shield className="w-3 h-3 text-green-400" />
                                            <span>Step 4: jira_create_ticket — incident SEC-1847 filed</span>
                                        </div>
                                        <div className="flex items-center gap-2 pt-1 border-t border-slate-700/50">
                                            <RefreshCw className="w-3 h-3 text-emerald-400" />
                                            <span className="text-emerald-300">Self-corrected 1 failed step. Task complete.</span>
                                        </div>
                                    </div>
                                </div>
                                <div className="rounded-xl bg-slate-800/50 border border-slate-700 p-4 flex flex-col">
                                    <span className="text-xs text-slate-500 mb-3">Connected Tools</span>
                                    <div className="flex-1 flex flex-col justify-center gap-2 text-xs">
                                        <div className="flex items-center gap-2 text-green-400"><Plug className="w-3 h-3" /> Splunk</div>
                                        <div className="flex items-center gap-2 text-green-400"><Plug className="w-3 h-3" /> CrowdStrike</div>
                                        <div className="flex items-center gap-2 text-green-400"><Plug className="w-3 h-3" /> Okta</div>
                                        <div className="flex items-center gap-2 text-green-400"><Plug className="w-3 h-3" /> Sentinel</div>
                                        <div className="flex items-center gap-2 text-green-400"><Plug className="w-3 h-3" /> Jira</div>
                                        <div className="flex items-center gap-2 text-green-400"><Plug className="w-3 h-3" /> PagerDuty</div>
                                        <div className="flex items-center gap-2 text-slate-500"><Plug className="w-3 h-3" /> + 3 more</div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* S4 continued: 6 Feature Cards */}
            <section id="how-it-works" className="py-24 bg-slate-900/30 border-y border-white/5">
                <div className="container mx-auto px-6">
                    <div className="text-center mb-16">
                        <h2 className="text-3xl font-bold mb-4">What Makes Zoro an Analyst, Not a Tool</h2>
                        <p className="text-slate-400 max-w-2xl mx-auto">
                            Zoro doesn't just follow playbooks — it <span className="text-white font-medium">reasons, plans, executes, self-corrects, and learns</span>.
                            Here's what happens under the hood.
                        </p>
                    </div>

                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-5xl mx-auto mb-16">
                        {[
                            {
                                icon: Brain,
                                title: 'LLM Task Planner',
                                desc: 'Goals are decomposed into multi-step investigation plans with dependencies, using security-trained reasoning.',
                                color: 'purple'
                            },
                            {
                                icon: Cpu,
                                title: 'Background Executor',
                                desc: 'Steps run concurrently in the background. High-risk actions pause for your approval.',
                                color: 'blue'
                            },
                            {
                                icon: RefreshCw,
                                title: 'Self-Correction',
                                desc: 'When a step fails, the agent replans automatically. No broken pipelines, ever.',
                                color: 'emerald'
                            },
                            {
                                icon: Database,
                                title: 'Persistent Memory',
                                desc: 'Incidents, patterns, and org context are remembered across sessions and investigations.',
                                color: 'amber'
                            },
                            {
                                icon: Sparkles,
                                title: 'Playbook Generation',
                                desc: 'Zoro writes its own playbooks on the fly based on what it learns — no templates, no manual authoring.',
                                color: 'pink'
                            },
                            {
                                icon: Network,
                                title: 'Knowledge Base',
                                desc: 'Upload your IR plans, compliance docs, org charts. Zoro compounds your institutional knowledge.',
                                color: 'cyan'
                            },
                        ].map((feature) => (
                            <div key={feature.title} className="p-6 rounded-2xl bg-slate-900 border border-slate-800 hover:border-slate-700 transition-all group">
                                <div className={`w-12 h-12 rounded-2xl bg-${feature.color}-500/10 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform`}>
                                    <feature.icon className={`w-6 h-6 text-${feature.color}-400`} />
                                </div>
                                <h4 className="font-bold text-white mb-2">{feature.title}</h4>
                                <p className="text-sm text-slate-400 leading-relaxed">{feature.desc}</p>
                            </div>
                        ))}
                    </div>

                    {/* Autonomy flow diagram */}
                    <div className="max-w-4xl mx-auto">
                        <div className="flex items-center justify-center gap-0 flex-wrap">
                            {[
                                { icon: AlertTriangle, label: 'Alert Detected', color: 'red' },
                                { icon: Brain, label: 'Goal Planned', color: 'purple' },
                                { icon: Rocket, label: 'Steps Executed', color: 'blue' },
                                { icon: RefreshCw, label: 'Self-Corrects', color: 'yellow' },
                                { icon: CheckCircle, label: 'Resolved', color: 'emerald' },
                            ].map((step, i) => (
                                <div key={step.label} className="flex items-center">
                                    <div className="flex flex-col items-center gap-2 px-4 py-3">
                                        <div className={`w-10 h-10 rounded-xl bg-${step.color}-500/10 flex items-center justify-center`}>
                                            <step.icon className={`w-5 h-5 text-${step.color}-400`} />
                                        </div>
                                        <span className="text-xs text-slate-400 font-medium">{step.label}</span>
                                    </div>
                                    {i < 4 && <ArrowRight className="w-4 h-4 text-slate-600 flex-shrink-0" />}
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            {/* Agentic ↔ Human-in-the-Loop Control Section */}
            <section className="py-24 bg-slate-900/50 border-y border-white/5">
                <div className="container mx-auto px-6">
                    <div className="text-center mb-16">
                        <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-amber-500/10 border border-amber-500/20 text-amber-400 text-sm font-medium mb-6">
                            <Gauge className="w-4 h-4" /> You Set the Rules
                        </div>
                        <h2 className="text-3xl md:text-4xl font-bold mb-4">
                            Your Rules. <span className="text-transparent bg-clip-text bg-gradient-to-r from-amber-400 to-primary-400">Your Control.</span>
                        </h2>
                        <p className="text-slate-400 text-lg max-w-2xl mx-auto">
                            Think of it like a new hire. Day one, you supervise everything.
                            As trust builds, you hand off more. Zoro works the same way —
                            from fully autonomous to human-in-the-loop, you decide.
                        </p>
                    </div>

                    {/* Visual Control Spectrum */}
                    <div className="max-w-3xl mx-auto mb-16">
                        <div className="relative rounded-2xl bg-slate-900 border border-slate-800 p-8">
                            {/* Mode Labels */}
                            <div className="flex items-center justify-between mb-8">
                                <div className="text-center">
                                    <div className="w-12 h-12 rounded-2xl bg-emerald-500/10 flex items-center justify-center mx-auto mb-2">
                                        <Zap className="w-6 h-6 text-emerald-400" />
                                    </div>
                                    <span className="text-sm font-bold text-emerald-400">Fully Autonomous</span>
                                    <p className="text-xs text-slate-500 mt-1 max-w-[120px]">Zoro handles everything</p>
                                </div>
                                <div className="text-center">
                                    <div className="w-12 h-12 rounded-2xl bg-amber-500/10 flex items-center justify-center mx-auto mb-2">
                                        <Eye className="w-6 h-6 text-amber-400" />
                                    </div>
                                    <span className="text-sm font-bold text-amber-400">Supervised</span>
                                    <p className="text-xs text-slate-500 mt-1 max-w-[120px]">High-risk actions need approval</p>
                                </div>
                                <div className="text-center">
                                    <div className="w-12 h-12 rounded-2xl bg-blue-500/10 flex items-center justify-center mx-auto mb-2">
                                        <Users className="w-6 h-6 text-blue-400" />
                                    </div>
                                    <span className="text-sm font-bold text-blue-400">Human-in-the-Loop</span>
                                    <p className="text-xs text-slate-500 mt-1 max-w-[120px]">Approve every action</p>
                                </div>
                            </div>

                            {/* Slider Track */}
                            <div className="relative h-3 rounded-full bg-slate-800 overflow-hidden">
                                <div className="absolute inset-0 bg-gradient-to-r from-emerald-500 via-amber-500 to-blue-500 opacity-30 rounded-full"></div>
                                <div className="absolute top-1/2 -translate-y-1/2 left-[20%] w-5 h-5 rounded-full bg-white shadow-lg shadow-white/25 border-2 border-emerald-400 animate-pulse"></div>
                            </div>

                            {/* Policy Examples */}
                            <div className="mt-8 grid grid-cols-3 gap-3 text-center">
                                <div className="px-3 py-2 rounded-lg bg-emerald-500/5 border border-emerald-500/10">
                                    <span className="text-xs text-slate-400">Log queries, enrichment</span>
                                </div>
                                <div className="px-3 py-2 rounded-lg bg-amber-500/5 border border-amber-500/10">
                                    <span className="text-xs text-slate-400">Block IPs, disable accounts</span>
                                </div>
                                <div className="px-3 py-2 rounded-lg bg-blue-500/5 border border-blue-500/10">
                                    <span className="text-xs text-slate-400">Delete data, modify configs</span>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Feature Cards */}
                    <div className="grid md:grid-cols-3 gap-6 max-w-5xl mx-auto">
                        <div className="p-6 rounded-2xl bg-slate-900 border border-slate-800 hover:border-amber-500/30 transition-all group">
                            <div className="w-12 h-12 rounded-2xl bg-amber-500/10 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                                <Zap className="w-6 h-6 text-amber-400" />
                            </div>
                            <h4 className="font-bold text-white mb-2">Instant Mode Switching</h4>
                            <p className="text-sm text-slate-400 leading-relaxed">
                                Flip from autonomous to human-approved in milliseconds. No restart, no reconfiguration.
                                The agent adapts <span className="text-white">mid-investigation</span>.
                            </p>
                        </div>

                        <div className="p-6 rounded-2xl bg-slate-900 border border-slate-800 hover:border-primary-500/30 transition-all group">
                            <div className="w-12 h-12 rounded-2xl bg-primary-500/10 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                                <GitBranch className="w-6 h-6 text-primary-400" />
                            </div>
                            <h4 className="font-bold text-white mb-2">Customizable Policies</h4>
                            <p className="text-sm text-slate-400 leading-relaxed">
                                Define rules in YAML: <span className="text-white">which tools are autonomous, which need approval</span>,
                                and which are completely off-limits. Your security posture, codified.
                            </p>
                        </div>

                        <div className="p-6 rounded-2xl bg-slate-900 border border-slate-800 hover:border-blue-500/30 transition-all group">
                            <div className="w-12 h-12 rounded-2xl bg-blue-500/10 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                                <Shield className="w-6 h-6 text-blue-400" />
                            </div>
                            <h4 className="font-bold text-white mb-2">Per-Action Granularity</h4>
                            <p className="text-sm text-slate-400 leading-relaxed">
                                Auto-approve Splunk searches but require sign-off for Okta account suspensions.
                                Control is <span className="text-white">per tool, per operation, per risk level</span>.
                            </p>
                        </div>
                    </div>
                </div>
            </section>

            {/* S6: Chat. Investigate. Resolve. */}
            <section className="py-24">
                <div className="container mx-auto px-6">
                    <div className="text-center mb-16">
                        <h2 className="text-3xl font-bold mb-4">Chat. Investigate. Resolve.</h2>
                        <p className="text-slate-400 max-w-2xl mx-auto">
                            Ask Zoro a question in chat. When the response is actionable,
                            hit <span className="text-white font-medium">Deploy as Task</span> and it runs autonomously in the background.
                        </p>
                    </div>

                    <div className="max-w-3xl mx-auto rounded-2xl bg-slate-900 border border-slate-800 p-6">
                        <div className="space-y-4">
                            {/* User message */}
                            <div className="flex gap-3 justify-end">
                                <div className="max-w-[70%] px-4 py-3 rounded-2xl rounded-br-md bg-blue-600 text-white text-sm">
                                    We got 47 failed logins from 192.168.1.100. Is this a brute force attack?
                                </div>
                                <div className="w-8 h-8 rounded-xl bg-blue-600 flex items-center justify-center flex-shrink-0">
                                    <MessageSquare className="w-4 h-4 text-white" />
                                </div>
                            </div>

                            {/* Agent response */}
                            <div className="flex gap-3">
                                <div className="w-8 h-8 rounded-xl bg-primary-600 flex items-center justify-center flex-shrink-0">
                                    <Bot className="w-4 h-4 text-white" />
                                </div>
                                <div className="max-w-[80%]">
                                    <div className="px-4 py-3 rounded-2xl rounded-bl-md bg-slate-800 border border-slate-700 text-slate-200 text-sm">
                                        <p className="mb-2">This looks like a <strong>credential stuffing attack</strong>. 47 failed attempts in 5 minutes from a single IP is well above the threshold.</p>
                                        <p>I recommend: querying Splunk for associated IPs, checking threat intel on 192.168.1.100, verifying the target account in Okta, and filing an incident ticket.</p>
                                    </div>

                                    <div className="mt-2 flex items-center justify-between">
                                        <span className="text-xs text-slate-500">2:34 PM</span>
                                        <button className="flex items-center gap-1.5 px-3 py-1 rounded-lg text-xs font-medium bg-primary-500/20 text-primary-300 border border-primary-500/30 hover:bg-primary-500/30 transition-all">
                                            <Rocket className="w-3.5 h-3.5" /> Deploy as Task
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Social Proof Bar */}
            <section className="py-12 bg-slate-900/50 border-y border-white/5">
                <div className="container mx-auto px-6 text-center">
                    <p className="text-sm text-slate-500 font-medium mb-4">TRUSTED BY SECURITY TEAMS</p>
                    <p className="text-lg text-slate-300 max-w-2xl mx-auto">
                        Currently in guided pilots with <span className="text-white font-medium">government agencies</span> and <span className="text-white font-medium">managed security providers</span>.
                    </p>
                </div>
            </section>

            {/* Integrations Section */}
            <IntegrationsSection />

            {/* Pricing Comparison */}
            <section className="pt-24 pb-8">
                <div className="container mx-auto px-6">
                    <div className="text-center mb-12">
                        <h2 className="text-3xl font-bold mb-4">The Real Cost of Doing Nothing</h2>
                        <p className="text-slate-400 max-w-2xl mx-auto">
                            Compare what you're paying now vs. what Zoro costs.
                        </p>
                    </div>

                    <div className="grid md:grid-cols-3 gap-6 max-w-4xl mx-auto mb-8">
                        {/* Human Analyst */}
                        <div className="rounded-2xl bg-slate-900/50 border border-slate-800 p-6 opacity-60">
                            <h3 className="text-lg font-bold text-red-400 mb-1">Human Analyst</h3>
                            <p className="text-3xl font-bold text-white mb-1">$120K+<span className="text-sm text-slate-500 font-normal">/yr</span></p>
                            <p className="text-xs text-slate-500 mb-4">Per analyst, fully loaded</p>
                            <ul className="space-y-2 text-xs text-slate-500">
                                <li>→ 3–6 months to hire</li>
                                <li>→ 18–24 month average tenure</li>
                                <li>→ Handles ~40 alerts/day</li>
                                <li>→ Doesn't work nights or weekends</li>
                                <li>→ Needs training, management, tools</li>
                            </ul>
                        </div>

                        {/* SOAR Platform */}
                        <div className="rounded-2xl bg-slate-900/50 border border-slate-800 p-6 opacity-60">
                            <h3 className="text-lg font-bold text-amber-400 mb-1">SOAR Platform</h3>
                            <p className="text-3xl font-bold text-white mb-1">$150K+<span className="text-sm text-slate-500 font-normal">/yr</span></p>
                            <p className="text-xs text-slate-500 mb-4">Enterprise license + staff</p>
                            <ul className="space-y-2 text-xs text-slate-500">
                                <li>→ 6–12 months to deploy</li>
                                <li>→ Requires playbook authors</li>
                                <li>→ Breaks when threats evolve</li>
                                <li>→ Manual maintenance forever</li>
                                <li>→ Still needs analysts to run it</li>
                            </ul>
                        </div>

                        {/* Zoro */}
                        <div className="rounded-2xl bg-gradient-to-br from-primary-900/30 to-slate-900 border-2 border-primary-500/30 p-6 shadow-xl shadow-primary-500/5">
                            <h3 className="text-lg font-bold text-primary-400 mb-1">Zoro</h3>
                            <p className="text-3xl font-bold text-white mb-1">$990<span className="text-sm text-slate-500 font-normal">/mo</span></p>
                            <p className="text-xs text-primary-400 mb-4">Starts at 2,000 investigations/mo</p>
                            <ul className="space-y-2 text-xs text-slate-300">
                                <li className="flex items-start gap-1"><span className="text-primary-400">✓</span> Deploys in 5 minutes</li>
                                <li className="flex items-start gap-1"><span className="text-primary-400">✓</span> No playbooks to write</li>
                                <li className="flex items-start gap-1"><span className="text-primary-400">✓</span> Self-corrects, adapts to new threats</li>
                                <li className="flex items-start gap-1"><span className="text-primary-400">✓</span> Works 24/7, no burnout</li>
                                <li className="flex items-start gap-1"><span className="text-primary-400">✓</span> Full audit trail included</li>
                            </ul>
                        </div>
                    </div>
                </div>
            </section>

            {/* Pricing + ROI Calculator */}
            <PricingSection onGetStarted={openModal} />

            {/* Deployment Timeline */}
            <section className="py-24">
                <div className="container mx-auto px-6">
                    <div className="text-center mb-16">
                        <h2 className="text-3xl font-bold mb-4">From First Login to Full Autonomy</h2>
                        <p className="text-slate-400 max-w-2xl mx-auto">
                            Zoro starts investigating the moment you connect your tools. No onboarding delays. No implementation projects.
                        </p>
                    </div>

                    <div className="max-w-5xl mx-auto space-y-0">
                        {[
                            { time: 'Day 1', title: 'Connect & Go', body: 'Connect your tools. Zoro starts investigating alerts immediately. First results in minutes, not months.', accent: 'from-primary-500 to-emerald-500' },
                            { time: 'Week 1', title: 'Building Trust', body: 'Zoro has already investigated hundreds of alerts. You\u2019re reviewing its work, tuning autonomy levels, and building trust \u2014 like any new hire.', accent: 'from-emerald-500 to-blue-500' },
                            { time: 'Week 2', title: 'Backlog Gone', body: 'Alert backlog is gone. Your analysts are focused on real threats and strategic work. Zoro handles the rest.', accent: 'from-blue-500 to-purple-500' },
                            { time: 'Ongoing', title: 'Always Compounding', body: 'Zoro gets sharper every day. Upload new docs, update policies, refine autonomy rules. Your institutional knowledge compounds instead of walking out the door.', accent: 'from-purple-500 to-primary-500' },
                        ].map((step, i) => (
                            <div key={i} className="flex items-stretch gap-6 md:gap-10">
                                {/* Left: step indicator + vertical line */}
                                <div className="flex flex-col items-center">
                                    <div className={`w-14 h-14 rounded-2xl bg-gradient-to-br ${step.accent} flex items-center justify-center text-white font-bold text-sm shadow-lg flex-shrink-0`}>
                                        {step.time.split(' ')[0] === 'Day' ? '01' : step.time.split(' ')[0] === 'Week' ? `W${step.time.split(' ')[1]}` : '\u221E'}
                                    </div>
                                    {i < 3 && (
                                        <div className="w-px flex-1 bg-gradient-to-b from-slate-700 to-slate-800 my-2"></div>
                                    )}
                                </div>

                                {/* Right: card */}
                                <div className={`flex-1 rounded-2xl bg-slate-900/50 border border-slate-800 p-6 ${i < 3 ? 'mb-4' : ''} hover:border-slate-700 transition-colors`}>
                                    <div className="flex items-center gap-3 mb-2">
                                        <span className="text-xs font-bold text-primary-400 uppercase tracking-wider">{step.time}</span>
                                    </div>
                                    <h3 className="font-bold text-white text-lg mb-1">{step.title}</h3>
                                    <p className="text-sm text-slate-400 leading-relaxed">{step.body}</p>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* S12: Security & Trust */}
            <section id="security" className="py-24 bg-slate-900/30 border-y border-white/5">
                <div className="container mx-auto px-6">
                    <div className="text-center mb-16">
                        <h2 className="text-3xl font-bold mb-4">Your Data Stays Yours</h2>
                        <p className="text-slate-400 max-w-2xl mx-auto">
                            Built with the security controls your team expects.
                        </p>
                    </div>

                    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 max-w-5xl mx-auto">
                        {[
                            { icon: Lock, title: 'Dedicated Data Isolation', body: 'Every customer gets dedicated, isolated infrastructure. No shared databases. No co-mingled data.' },
                            { icon: Shield, title: 'Never Train on Your Data', body: 'We never use your data to train AI models. Your security data stays in your environment.' },
                            { icon: GitBranch, title: 'Full Audit Trail', body: 'Every decision, tool call, and action is logged for compliance. Complete transparency.' },
                            { icon: Fingerprint, title: 'SOC 2 Type II (In Progress)', body: 'Actively working toward SOC 2 Type II certification. Enterprise-grade compliance is on our roadmap.' },
                            { icon: Eye, title: 'Complete Transparency', body: 'See every step of every investigation. Full reasoning traces, tool calls, and decision logs.' },
                            { icon: Gauge, title: 'Configurable Clearance', body: 'You define exactly what Zoro can and can\'t do. Per-tool, per-action, per-risk level.' },
                        ].map((item) => (
                            <div key={item.title} className="p-6 rounded-2xl bg-slate-900 border border-slate-800 hover:border-emerald-500/20 transition-all">
                                <item.icon className="w-8 h-8 text-emerald-400 mb-4" />
                                <h3 className="font-bold mb-2">{item.title}</h3>
                                <p className="text-sm text-slate-400">{item.body}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* S13: FAQ */}
            <section className="py-24">
                <div className="container mx-auto px-6">
                    <div className="text-center mb-16">
                        <h2 className="text-3xl font-bold mb-4">Frequently Asked Questions</h2>
                    </div>

                    <div className="max-w-3xl mx-auto space-y-4">
                        {[
                            { q: 'Is Zoro a chatbot?', a: 'No. Zoro is a fully autonomous agent. It doesn\'t wait for you to ask questions — it receives alerts, plans investigations, executes across your tools, and resolves incidents end-to-end.' },
                            { q: 'How is Zoro different from other AI security tools?', a: 'Most "AI for security" tools are copilots — they sit next to your analyst and suggest next steps. Zoro is an autonomous agent that receives alerts, plans its own investigations, executes across your tools, self-corrects when steps fail, and resolves incidents start to finish. Your analysts aren\'t doing the same job faster. They\'re doing a different, more strategic job entirely.' },
                            { q: 'Can Zoro use our existing playbooks and documentation?', a: 'Yes. Upload your IR plans, compliance docs, runbooks, and org context into Zoro\'s knowledge base. Zoro compounds your institutional knowledge and uses it to inform investigations — but it also writes its own playbooks on the fly when it encounters novel situations.' },
                            { q: 'What happens if Zoro makes a mistake?', a: 'Zoro self-corrects when steps fail. And you control exactly what it can do autonomously — high-risk actions can require human approval. Full audit trails let you review every action.' },
                            { q: 'How long does deployment take?', a: 'Under 5 minutes for most setups. Connect your SIEM and identity tools via API keys, set your autonomy policy, and Zoro starts investigating.' },
                            { q: 'Does Zoro replace my SOC team?', a: 'No. Zoro handles the 80% of alerts that don\'t need human judgment so your analysts can focus on novel threats, threat hunting, and strategic security work.' },
                            { q: 'What integrations are supported?', a: 'Splunk, CrowdStrike, Sentinel, Okta, ServiceNow, Jira, PagerDuty, SentinelOne, and AWS — with custom connectors available on Enterprise plans.' },
                            { q: 'Is my data safe?', a: 'Every plan includes dedicated data isolation. We never see your security data, and we never use it to train models.' },
                            { q: 'Can I try Zoro before committing?', a: 'Yes. We offer guided pilot programs where we deploy Zoro in your environment and show you real results before you commit.' },
                            { q: 'What\'s the difference between Zoro and SOAR platforms?', a: 'SOAR requires months of playbook authoring and breaks when threats evolve. Zoro writes its own playbooks, adapts to novel attacks, and self-corrects — no manual maintenance.' },
                        ].map((item) => (
                            <details key={item.q} className="group rounded-2xl bg-slate-900 border border-slate-800 overflow-hidden">
                                <summary className="flex items-center justify-between p-6 cursor-pointer text-white font-medium hover:bg-slate-800/50 transition-colors">
                                    {item.q}
                                    <Plus className="w-5 h-5 text-slate-500 group-open:hidden flex-shrink-0" />
                                    <Minus className="w-5 h-5 text-primary-400 hidden group-open:block flex-shrink-0" />
                                </summary>
                                <div className="px-6 pb-6 text-sm text-slate-400 leading-relaxed">
                                    {item.a}
                                </div>
                            </details>
                        ))}
                    </div>
                </div>
            </section>

            {/* S14: Final CTA */}
            <section className="py-24 text-center container mx-auto px-6">
                <div className="max-w-4xl mx-auto bg-gradient-to-b from-primary-900/30 to-slate-900 rounded-3xl p-16 border border-primary-500/20 relative overflow-hidden">
                    <div className="absolute inset-0 bg-grid-white/[0.02] bg-[size:20px_20px]"></div>
                    <div className="hidden md:block absolute top-0 left-1/2 -translate-x-1/2 w-96 h-96 bg-primary-500/20 rounded-full blur-[60px] -z-10"></div>

                    <div className="relative z-10">
                        <h2 className="text-4xl lg:text-5xl font-bold mb-6">Ready to Onboard<br />Your Newest Security Analyst?</h2>
                        <p className="text-slate-400 mb-10 text-lg max-w-xl mx-auto">
                            Start a guided pilot and see Zoro investigate real alerts in your environment.
                        </p>
                        <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                            <button
                                onClick={openModal}
                                className="px-12 py-5 rounded-xl bg-white text-slate-900 font-bold text-lg hover:bg-slate-100 transition-colors shadow-xl"
                            >
                                Start a Guided Pilot
                            </button>
                            <button
                                onClick={() => setShowInteractiveDemo(true)}
                                className="px-8 py-4 rounded-xl bg-slate-800 hover:bg-slate-700 border border-slate-600 text-white font-medium transition-all flex items-center gap-2"
                            >
                                <Eye className="w-5 h-5" /> See Zoro Investigate
                            </button>
                        </div>
                        <p className="mt-6 text-xs text-slate-500">5-minute setup • Works with your existing tools • Dedicated data isolation</p>
                    </div>
                </div>
            </section>

            {/* Footer */}
            <footer className="border-t border-white/5 py-16 bg-slate-950">
                <div className="container mx-auto px-6">
                    <div className="grid md:grid-cols-4 gap-12 mb-12">
                        <div>
                            <div className="flex items-center gap-2 mb-4">
                                <img src={logoImage} alt="Zoro" className="w-8 h-8" />
                                <span className="font-bold text-lg">Zoro</span>
                            </div>
                            <p className="text-sm text-slate-500">Your newest security analyst.</p>
                        </div>
                        <div>
                            <h4 className="font-semibold text-white mb-4 text-sm">Product</h4>
                            <ul className="space-y-2 text-sm text-slate-500">
                                <li><a href="#platform" className="hover:text-white transition-colors">Platform</a></li>
                                <li><a href="#how-it-works" className="hover:text-white transition-colors">How It Works</a></li>
                                <li><a href="#integrations" className="hover:text-white transition-colors">Integrations</a></li>
                                <li><a href="#pricing" className="hover:text-white transition-colors">Pricing</a></li>
                            </ul>
                        </div>
                        <div>
                            <h4 className="font-semibold text-white mb-4 text-sm">Company</h4>
                            <ul className="space-y-2 text-sm text-slate-500">
                                <li><a href="#about" className="hover:text-white transition-colors">About</a></li>
                                <li><a href="#blog" className="hover:text-white transition-colors">Blog</a></li>
                                <li><a href="#careers" className="hover:text-white transition-colors">Careers</a></li>
                                <li><a href="#contact" className="hover:text-white transition-colors">Contact</a></li>
                            </ul>
                        </div>
                        <div>
                            <h4 className="font-semibold text-white mb-4 text-sm">Legal</h4>
                            <ul className="space-y-2 text-sm text-slate-500">
                                <li><a href="#privacy" className="hover:text-white transition-colors">Privacy Policy</a></li>
                                <li><a href="#terms" className="hover:text-white transition-colors">Terms of Service</a></li>
                                <li><a href="#security" className="hover:text-white transition-colors">Security</a></li>
                            </ul>
                        </div>
                    </div>
                    <div className="border-t border-white/5 pt-8 flex flex-col md:flex-row justify-between items-center text-slate-500 text-sm">
                        <span>© 2026 Zoro Inc. All rights reserved.</span>
                        <div className="flex gap-6 mt-4 md:mt-0">
                            <a href="mailto:hello@getzoro.ai" className="hover:text-white">hello@getzoro.ai</a>
                        </div>
                    </div>
                </div>
            </footer>
        </div>
    );
}
